import type {
  BasementConfig,
  HouseBodyConfig,
  LotConfig,
  SlabHoleConfig,
  StairConfig,
} from '@/model/schema';
import type { Rng } from './rng';
import { findStairSlotInStory, type Rect, type StoryPlanData } from './stairsPlanner';

/**
 * Basements are modelled as a single large below-grade room with their
 * own footprint and a stair flight up to the ground floor.
 *
 * Footprint
 * ---------
 * In real builds the basement is sometimes EXACTLY under the house but
 * just as often extends out under a porch, a garage, or even the front
 * driveway. We model that by giving the basement its own footprint
 * (anchored in body-local coords). It defaults to the body footprint
 * but is randomly nudged out by 0..2 m on one or two sides as long as
 * the lot has room (we keep a 0.5 m clearance from the lot edge so the
 * basement walls never poke past the property line).
 *
 * Stair placement
 * ---------------
 * The stair lands on L0, so the slab hole — and therefore the stair
 * footprint — must sit fully inside one of L0's rooms AND clear every
 * partition-door and exterior-door approach zone on L0. We reuse the
 * same slot-finding logic as the inter-story stair planner (via
 * `findStairSlotInStory`) so the constraints stay in lockstep.
 *
 * If the L0 plan is so tight that no slot fits, the caller should
 * treat `generateBasement` as returning `null` and skip the basement
 * entirely.
 */
const BASEMENT_HEIGHT = 2.4;
const BASEMENT_FLOOR_THICKNESS = 0.25;
const BASEMENT_WALL_THICKNESS = 0.25;
/** Minimum gap between the basement outer wall and the lot's edge so
 *  excavation doesn't break the property line. */
const LOT_BASEMENT_MARGIN = 0.5;

export interface BasementResult {
  basement: BasementConfig;
  /** Slab hole to punch in the ground-floor slab for the stair well. */
  groundSlabHole: SlabHoleConfig;
  /** Hole to punch in the lot base plate so the user can see down into
   *  the basement when peeling the L0 slab. Same XZ as `groundSlabHole`
   *  but expressed in lot-local (origin-centered) coords. */
  basePlateHole: SlabHoleConfig;
}

export function generateBasement(
  rng: Rng,
  body: HouseBodyConfig,
  lot: LotConfig,
  story0: StoryPlanData,
  extraForbidden: Rect[] = [],
): BasementResult | null {
  const slot = findStairSlotInStory(
    rng.fork('basement-slot'),
    body,
    story0,
    extraForbidden,
  );
  if (!slot) return null;

  // Decide if the basement extends beyond the body. We pick a random
  // outward extension per side, capped by the lot setbacks (so we
  // don't excavate into a sidewalk or under the fence). 0 m on most
  // sides is fine — we just want it to look "real" some of the time.
  const halfLotW = lot.width / 2;
  const halfLotD = lot.depth / 2;
  const bodyLeftWorld = body.originX;
  const bodyRightWorld = body.originX + body.footprintWidth;
  const bodyFrontWorld = body.originZ;
  const bodyBackWorld = body.originZ + body.footprintDepth;
  const maxExtLeft = Math.max(0, bodyLeftWorld - (-halfLotW + LOT_BASEMENT_MARGIN));
  const maxExtRight = Math.max(0, halfLotW - LOT_BASEMENT_MARGIN - bodyRightWorld);
  const maxExtFront = Math.max(0, bodyFrontWorld - (-halfLotD + LOT_BASEMENT_MARGIN));
  const maxExtBack = Math.max(0, halfLotD - LOT_BASEMENT_MARGIN - bodyBackWorld);

  // Minimum padding so the basement's INTERIOR wall faces line up with
  // (or sit outside) the body's outer perimeter. The stair slot solver
  // above measures clearances against the BODY's outer walls; without
  // this pad the basement wall thickness eats into that budget and
  // the stair ends up hugging a basement wall's inner face (both at
  // the downhill step-off and along the lateral side — user-reported
  // "basement stair ends at a wall"). Clamped per side so we never
  // cross the lot setback.
  const minPad = BASEMENT_WALL_THICKNESS;
  const ext = (cap: number) => {
    if (cap <= 0) return 0;
    const rand = rng.chance(0.35) ? Math.min(cap, rng.range(0.5, 2.0)) : 0;
    return Math.min(cap, Math.max(minPad, rand));
  };
  const extLeft = ext(maxExtLeft);
  const extRight = ext(maxExtRight);
  const extFront = ext(maxExtFront);
  const extBack = ext(maxExtBack);

  const footprintWidth = body.footprintWidth + extLeft + extRight;
  const footprintDepth = body.footprintDepth + extFront + extBack;
  const originX = -extLeft;
  const originZ = -extFront;

  const stair: StairConfig = {
    toLevel: 0,
    x: slot.x,
    z: slot.z,
    width: slot.width,
    depth: slot.depth,
    climbDir: slot.climbDir,
  };

  const basement: BasementConfig = {
    height: BASEMENT_HEIGHT,
    floorThickness: BASEMENT_FLOOR_THICKNESS,
    wallThickness: BASEMENT_WALL_THICKNESS,
    footprintWidth,
    footprintDepth,
    originX,
    originZ,
    stair,
  };

  const groundSlabHole: SlabHoleConfig = {
    x: stair.x,
    z: stair.z,
    width: stair.width,
    depth: stair.depth,
  };

  // Lot-local coords: origin is the lot center. Body-local origin is the
  // body's lower-left corner in world coords. Since the body itself is
  // anchored in the world via body.originX/originZ, body-local (X,Z) =
  // world (X - body.originX, Z - body.originZ). The lot is also in
  // world coords centred on origin, so lot-local = world.
  const basePlateHole: SlabHoleConfig = {
    x: body.originX + stair.x,
    z: body.originZ + stair.z,
    width: stair.width,
    depth: stair.depth,
  };

  return { basement, groundSlabHole, basePlateHole };
}
